import { SUPPORTED_MODEL_TYPES } from "src/contexts/_shared/domain/SupportedModelTypes";
import { Badge } from "../../Badge";

import { useTranslations } from "src/apps/hooks/useTranslations";
import { Model } from "src/contexts/documentationArea/domain/Model";
import { useTheme } from "styled-components";
import { filterNulls } from "src/contexts/_shared/utils";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { OrderItem } from "src/contexts/documentationArea/domain/OrderItem";
import { colors } from "@permisso/permisso-components";

export const computeBadgesByOrderType = (order: OrderItem) => {
  if (order.getType() !== "model") return;

  const model = order.getRequest() as Model;
  const badges = [getModelNumberBadge(model), getDateBadge(model)];

  return filterNulls(badges);
};

const getModelNumberBadge = (model: Model) => {
  const { t } = useTranslations();
  const { blackColor } = useTheme();
  const modelNumber = getModelNumberBadgePerModelType[model.type];
  const modelTranslation = modelNumber
    ? t("documentation_area.badge.model_number", {
        modelNumber,
      })
    : null;
  if (modelTranslation)
    return (
      <Badge
        key={modelNumber}
        text={modelTranslation}
        backgroundColor={colors.grey300}
        textColor={blackColor}
      />
    );
  return null;
};

const getDateBadge = (model: Model) => {
  const { t } = useTranslations();
  const { primaryColor, blackColor } = useTheme();

  if (model.month) {
    return (
      <Badge
        key={`month-${model.month}`}
        text={t(`documentation_area.badge.short_month.${model.month}`, {
          year: model.year!.toString(),
        })}
        backgroundColor={colors.grey300}
        textColor={blackColor}
        iconColor={primaryColor}
        leftIconSVG={ICON_PATHS.CALENDAR}
      />
    );
  }

  if (model.quarter) {
    return (
      <Badge
        key={`quarter-${model.quarter}`}
        text={t(`documentation_area.badge.short_quarter.${model.quarter}`, {
          year: model.year!.toString(),
        })}
        backgroundColor={colors.grey300}
        textColor={blackColor}
        iconColor={primaryColor}
        leftIconSVG={ICON_PATHS.CALENDAR}
      />
    );
  }

  if (model.year) {
    return (
      <Badge
        key="year"
        text={model.year.toString()}
        backgroundColor={colors.grey300}
        textColor={blackColor}
        iconColor={primaryColor}
        leftIconSVG={ICON_PATHS.CALENDAR}
      />
    );
  }

  return null;
};

const getModelNumberBadgePerModelType: {
  [key in SUPPORTED_MODEL_TYPES]: string | null;
} = {
  AEAT_100_RENTA: "100",
  AEAT_151_REGIMEN_ESPECIAL: "151",
  AEAT_036_CENSO_EMPRESARIOS: "036",
  AEAT_111_IRPF_RENDIMIENTOS_TRABAJO_AUTOLIQUIDACION: "111",
  AEAT_115_IRPF_RENDIMIENTO_INMUEBLES: "115",
  AEAT_130_IRPF_DIRECTA: "130",
  AEAT_131_IRPF_OBJETIVA: "131",
  AEAT_180_IRPF_RENDIMIENTO_INMUEBLES_RESUMEN: "180",
  AEAT_190_IRPF_RENDIMIENTOS_TRABAJO_RESUMEN: "190",
  AEAT_303_IVA_AUTOLIQUIDACION: "303",
  AEAT_309_IVA_LIQUIDACION_NO_PERIODICA: "309",
  AEAT_347_OPERACIONES_TERCEROS: "347",
  AEAT_349_IVA_OPERACIONES_INTRACOMUNITARIAS: "349",
  AEAT_200_IMPUESTO_SOCIEDADES: "200",
  AEAT_390_IVA_RESUMEN: "390",
  AEAT_714_IMPUESTO_PATRIMONIO: "714",
  AEAT_CERT_CENSAL: null,
  AEAT_CERT_IRPF: null,
  AEAT_CERT_OBLIG_TRIB: null,
  AEAT_IRPF_DATOS_FISCALES: null,
  AEAT_CERT_CENSO_ACTIVIDADES_ECONOMICAS: null,
  AEAT_DATOS_CENSALES: null,
  AEAT_DETALLE_DEUDAS: null,
  DGT_VEHICLE_DATA: null,
  DGT_INFORME_DATOS_CONDUCTOR: null,
  SEG_SOCIAL_RESUMEN_SITUACION: null,
  SEG_SOCIAL_BASE_COTIZACIONES: null,
  SEG_SOCIAL_CERT_INTEGRAL_PRESTACIONES: null,
  SEG_SOCIAL_CERT_OBLIG: null,
  SEG_SOCIAL_VIDA_LABORAL: null,
  SEG_SOCIAL_DATOS_TRABAJO_AUTONOMO: null,
  SEG_SOCIAL_SITUACION_ACTUAL: null,
  SEG_SOCIAL_DATOS_PERSONALES: null,
  SEG_SOCIAL_DETALLE_DEUDAS: null,
  CARP_CIUD_JUST_EMPADRONAMIENTO: null,
  CARP_CIUD_JUST_AUSENCIA_ANT_PENALES: null,
  CARP_CIUD_CERT_CATASTRO: null,
  CARP_CIUD_CERT_NACIMIENTO: null,
  CARP_CIUD_DATOS_PERSONALES: null,
  CARP_CIUD_TITULOS_UNIVERSITARIOS: null,
  CARP_CIUD_TITULOS_NO_UNIVERSITARIOS: null,
  NAVARRA_F93: "93",
  VIZCAYA_100_RENTA: "100",
  GUIPUZCOA_109_RENTA: "109",
  ALAVA_100_RENTA: "100",
  HACIENDA_RENTA: null,
  BDE_CIRBE: null,
};
